// Service for managing user shortcuts stored in localStorage

export interface Shortcut {
  id: string;
  title: string;
  path: string;
  icon: string;
  group: 'plukk-pakk' | 'varemottak' | 'lager' | 'verktoy' | 'grunndata' | 'historikk' | 'vareplassering' | 'transaksjoner' | 'lagerstilling';
  order: number;
}

const STORAGE_KEY = 'wms_shortcuts';

class ShortcutService {
  /**
   * Get all shortcuts for current user
   */
  getShortcuts(): Shortcut[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch (error) {
      console.error('Failed to load shortcuts:', error);
      return [];
    }
  }

  /**
   * Add a new shortcut
   */
  addShortcut(shortcut: Omit<Shortcut, 'order'>): void {
    const shortcuts = this.getShortcuts();
    const maxOrder = shortcuts.length > 0 ? Math.max(...shortcuts.map(s => s.order)) : 0;

    const newShortcut: Shortcut = {
      ...shortcut,
      order: maxOrder + 1
    };

    shortcuts.push(newShortcut);
    this.saveShortcuts(shortcuts);
  }

  /**
   * Remove a shortcut by id
   */
  removeShortcut(id: string): void {
    const shortcuts = this.getShortcuts().filter(s => s.id !== id);
    this.saveShortcuts(shortcuts);
  }

  /**
   * Check if a shortcut exists
   */
  hasShortcut(id: string): boolean {
    return this.getShortcuts().some(s => s.id === id);
  }

  /**
   * Toggle shortcut (add if not exists, remove if exists)
   */
  toggleShortcut(shortcut: Omit<Shortcut, 'order'>): boolean {
    if (this.hasShortcut(shortcut.id)) {
      this.removeShortcut(shortcut.id);
      return false; // Removed
    } else {
      this.addShortcut(shortcut);
      return true; // Added
    }
  }

  /**
   * Get shortcuts grouped by category
   */
  getGroupedShortcuts(): Record<string, Shortcut[]> {
    const shortcuts = this.getShortcuts();
    const grouped: Record<string, Shortcut[]> = {};

    shortcuts.forEach(shortcut => {
      if (!grouped[shortcut.group]) {
        grouped[shortcut.group] = [];
      }
      grouped[shortcut.group].push(shortcut);
    });

    // Sort each group by order
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.order - b.order);
    });

    return grouped;
  }

  /**
   * Save shortcuts to localStorage
   */
  private saveShortcuts(shortcuts: Shortcut[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(shortcuts));
      // Dispatch custom event so other components can react
      window.dispatchEvent(new CustomEvent('shortcuts-changed'));
    } catch (error) {
      console.error('Failed to save shortcuts:', error);
    }
  }

  /**
   * Clear all shortcuts
   */
  clearShortcuts(): void {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('shortcuts-changed'));
  }
}

export default new ShortcutService();
